import React from "react";
import { Tooltip, type MenuProps } from "antd";
import type { TFunction } from "i18next";
import {
  TACHOGRAPH_ICON,
  SidebarCustomersIcon,
  SidebarDashboardIcon,
  SidebarDeviceIcon,
  SidebarSettingsIcon,
  SidebarUserManagementIcon,
  SidebarWarehouseIcon,
} from "src/assets/svgs";
import { hasAdministrationPermission } from "src/util/administrationPermission";

const icons = {
  tachograph: TACHOGRAPH_ICON,
  dashboard: SidebarDashboardIcon,
  customer: SidebarCustomersIcon,
  device: SidebarDeviceIcon,
  userManagement: SidebarUserManagementIcon,
  warehouse: SidebarWarehouseIcon,
  settings: SidebarSettingsIcon,
};

const renderMenuIcon = (Icon: (typeof icons)[keyof typeof icons]) => {
  const SidebarIcon = Icon as unknown as React.ComponentType<React.SVGProps<SVGSVGElement>>;
  return <SidebarIcon className="sidebar-menu-item-icon" aria-hidden />;
};

/** Matches fdmr CustomMenuLink: 600 on parents, font-medium on submenu children */
const MenuLabel: React.FC<{ text: string; child?: boolean }> = ({ text, child }) => {
  const labelRef = React.useRef<HTMLSpanElement>(null);
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  const handleOpenChange = (open: boolean) => {
    let element: HTMLElement | null = labelRef.current;
    let truncated = false;

    while (element && !element.classList.contains("ant-menu")) {
      const style = window.getComputedStyle(element);
      if (
        style.textOverflow === "ellipsis" &&
        element.scrollWidth > element.clientWidth
      ) {
        truncated = true;
        break;
      }
      element = element.parentElement;
    }

    setTooltipOpen(open && Boolean(child) && truncated);
  };

  return (
    <Tooltip
      title={child ? text : undefined}
      placement="right"
      zIndex={1400}
      open={tooltipOpen}
      onOpenChange={handleOpenChange}
    >
      <span
        ref={labelRef}
        className={
          child ? "custom-menu-text font-medium text-[14px]" : "custom-menu-text font-[600] text-[14px]"
        }
      >
        {text}
      </span>
    </Tooltip>
  );
};

/** key -> route (crm urls) */
export const routeByMenuKey: Record<string, string> = {
  dashboard: "/",
  all_clients: "/customers",
  "raw-data-log": "/customers/raw-data-log",
  audit_log: "/customers/audit-log-data",
  driver_activities: "/customers/ddd-files",
  tachograph_cards: "/customers/tachograph-cards",
  tachograph_download_log: "/tachograph/download-log",
  tachograph_models: "/tachograph/models",
  device_management: "/device-management",
  device_connection: "/device-management/connect-to-sim",
  "users-list": "/user-management/users",
  "create-user": "/user-management/add-or-edit-user",
  import_units: "/warehouse-management/import-unit",
  all_devices: "/warehouse-management/all-units",
  add_warehouse: "/warehouse-management/warehouse",
  add_position_rules: "/warehouse-management/position-rules",
  vehicle_settings: "/administration/settings/vehicle-settings/brand",
  tire_settings: "/administration/settings/tire-settings/brand",
  units_settings: "/administration/settings/units-settings/brand",
  customers_settings: "/administration/settings/customers-settings/type",
  add_model_propieties: "/administration/setups/add-model-properties",
  model_propieties: "/administration/setups/model-propieties",
  temperature_sensor_types: "/administration/setups/temperature-sensor-types",
  switch_on_off_state: "/administration/setups/switch-on-off-state",
  relay_status_type: "/administration/setups/relay-status-type",
  parameter_group: "/administration/setups/parameter-groups",
  parameters: "/administration/setups/parameters",
  business_activities: "/administration/general-setups/client/type-of-client-business-activities",
};

const routeSelectionEntries = [
  ...Object.entries(routeByMenuKey).map(([key, path]) => ({ key, path })),
  { key: "vehicle_settings", path: "/administration/settings/vehicle-settings" },
  { key: "tire_settings", path: "/administration/settings/tire-settings" },
  { key: "units_settings", path: "/administration/settings/units-settings" },
  { key: "customers_settings", path: "/administration/settings/customers-settings" },
  { key: "vehicle_settings", path: "/administration/setups/vehicle-make" },
  { key: "vehicle_settings", path: "/administration/setups/vehicle-model" },
  { key: "vehicle_settings", path: "/administration/general-setups/vehicle/type" },
  { key: "tire_settings", path: "/administration/setups/tire-make" },
  { key: "tire_settings", path: "/administration/setups/tire-model" },
  { key: "tire_settings", path: "/administration/setups/tire-measure" },
  { key: "tire_settings", path: "/administration/setups/tire-preset" },
  { key: "units_settings", path: "/administration/setups/unit-brand" },
  { key: "units_settings", path: "/administration/setups/unit-models" },
  { key: "customers_settings", path: "/administration/general-setups/client/type" },
  { key: "customers_settings", path: "/administration/general-setups/client/source" },
  { key: "customers_settings", path: "/administration/general-setups/client/position" },
];

export const getMenuSelection = (
  pathname: string
): { selectedKeys: string[]; openKeys: string[] } => {
  const match = routeSelectionEntries
    .filter(({ path }) =>
      path === "/" ? pathname === "/" : pathname === path || pathname.startsWith(`${path}/`)
    )
    .sort((a, b) => b.path.length - a.path.length)[0];

  if (!match) {
    return { selectedKeys: ["dashboard"], openKeys: [] };
  }

  const openKeysByChild: Record<string, string[]> = {
    all_clients: ["clients"],
    "raw-data-log": ["clients"],
    audit_log: ["clients"],
    driver_activities: ["tachograph"],
    tachograph_cards: ["tachograph"],
    tachograph_download_log: ["tachograph"],
    tachograph_models: ["tachograph"],
    device_management: ["manage_units"],
    device_connection: ["manage_units"],
    "users-list": ["user_management"],
    "create-user": ["user_management"],
    import_units: ["warehouse"],
    all_devices: ["warehouse"],
    add_warehouse: ["warehouse"],
    add_position_rules: ["warehouse"],
    vehicle_settings: ["administrator", "settings_vehicles"],
    tire_settings: ["administrator", "settings_vehicles"],
    units_settings: ["administrator", "settings_units"],
    customers_settings: ["administrator", "settings_customers"],
    add_model_propieties: ["administrator", "settings_vehicles"],
    model_propieties: ["administrator", "settings_units"],
    temperature_sensor_types: ["administrator", "settings_units"],
    switch_on_off_state: ["administrator", "settings_units"],
    relay_status_type: ["administrator", "settings_units"],
    parameter_group: ["administrator", "settings_units"],
    parameters: ["administrator", "settings_units"],
    business_activities: ["administrator", "settings_customers"],
  };

  return {
    selectedKeys: [match.key],
    openKeys: openKeysByChild[match.key] ?? [],
  };
};

export const getSidebarMenuItems = (t: TFunction): MenuProps["items"] => {
  const canViewAdministration = hasAdministrationPermission("view");

  return [
    {
      key: "dashboard",
      icon: renderMenuIcon(icons.dashboard),
      label: <MenuLabel text={t("menu_dashboard")} />,
    },
    {
      key: "clients",
      icon: renderMenuIcon(icons.customer),
      label: <MenuLabel text={t("menu_customers")} />,
      children: [
        {
          key: "all_clients",
          label: <MenuLabel child text={t("all_clients")} />,
        },
        {
          key: "raw-data-log",
          label: <MenuLabel child text={t("raw-data-log")} />,
        },
        {
          key: "audit_log",
          label: <MenuLabel child text={t("audit_log")} />,
        },
      ],
    },
    {
      key: "tachograph",
      popupClassName: "tachograph-submenu-popup",
      icon: renderMenuIcon(icons.tachograph),
      label: <MenuLabel text={t("menu_tachograph")} />,
      children: [
        {
          key: "driver_activities",
          label: <MenuLabel child text={t("driver_activities")} />,
        },
        {
          key: "tachograph_cards",
          label: <MenuLabel child text={t("tachograph_cards")} />,
        },
        {
          key: "tachograph_download_log",
          label: <MenuLabel child text={t("tachograph_download_log")} />,
        },
        {
          key: "tachograph_models",
          label: <MenuLabel child text={t("tachograph_models")} />,
        },
      ],
    },
    {
      key: "user_management",
      icon: renderMenuIcon(icons.userManagement),
      label: <MenuLabel text={t("menu_user_management")} />,
      children: [
        { key: "users-list", label: <MenuLabel child text={t("users")} /> },
        {
          key: "create-user",
          label: <MenuLabel child text={t("create-user")} />,
        },
      ],
    },
    {
      key: "manage_units",
      icon: renderMenuIcon(icons.device),
      label: <MenuLabel text={t("menu_manage_units")} />,
      children: [
        {
          key: "device_management",
          label: <MenuLabel child text={t("menu_device_management_panel")} />,
        },
        {
          key: "device_connection",
          label: <MenuLabel child text={t("menu_device_connection")} />,
        },
      ],
    },
    {
      key: "warehouse",
      icon: renderMenuIcon(icons.warehouse),
      label: <MenuLabel text={t("menu_warehouse")} />,
      children: [
        {
          key: "import_units",
          label: <MenuLabel child text={t("modules")} />,
        },
        {
          key: "all_devices",
          label: <MenuLabel child text={t("all_devices")} />,
        },
        {
          key: "add_warehouse",
          label: <MenuLabel child text={t("warehouse")} />,
        },
        {
          key: "add_position_rules",
          label: <MenuLabel child text={t("position_rules")} />,
        },
      ],
    },
    ...(canViewAdministration
      ? [
          {
            key: "administrator",
            icon: renderMenuIcon(icons.settings),
            label: <MenuLabel text={t("menu_settings")} />,
            children: [
              {
                key: "settings_vehicles",
                label: <MenuLabel child text={t("menu_vehicles")} />,
                children: [
                  {
                    key: "vehicle_settings",
                    label: <MenuLabel child text={t("menu_vehicles_settings")} />,
                  },
                  {
                    key: "tire_settings",
                    label: <MenuLabel child text={t("menu_tire_settings")} />,
                  },
                  {
                    key: "add_model_propieties",
                    label: <MenuLabel child text={t("menu_add_model_propieties")} />,
                  },
                ],
              },
              {
                key: "settings_units",
                label: <MenuLabel child text={t("menu_units")} />,
                children: [
                  {
                    key: "units_settings",
                    label: <MenuLabel child text={t("menu_unit_settings")} />,
                  },
                  {
                    key: "model_propieties",
                    label: <MenuLabel child text={t("menu_model_propieties")} />,
                  },
                  {
                    key: "parameters",
                    label: <MenuLabel child text={t("menu_parameters")} />,
                  },
                  {
                    key: "parameter_group",
                    label: <MenuLabel child text={t("menu_parameters_group")} />,
                  },
                  {
                    key: "temperature_sensor_types",
                    label: <MenuLabel child text={t("menu_temperature_sensor_types")} />,
                  },
                  {
                    key: "switch_on_off_state",
                    label: <MenuLabel child text={t("menu_switch_on_off_state_types")} />,
                  },
                  {
                    key: "relay_status_type",
                    label: <MenuLabel child text={t("menu_relay_status")} />,
                  },
                ],
              },
              {
                key: "settings_customers",
                label: <MenuLabel child text={t("menu_customers")} />,
                children: [
                  {
                    key: "customers_settings",
                    label: <MenuLabel child text={t("menu_customers_settings")} />,
                  },
                  {
                    key: "business_activities",
                    label: <MenuLabel child text={t("menu_business_activities")} />,
                  },
                ],
              },
            ],
          },
        ]
      : []),
  ];
};
